import React from 'react';
import { cn } from '../../lib/utils'; 
import { Check, Star, ChevronDown, ChevronUp } from 'lucide-react';

interface ComparisonOption {
  title: string;
  money: string;
  time: string;
  quality: string;
  speed: string;
  risk: string;
  recommended?: boolean;
}

interface ComparisonCardsState {
  expanded: boolean;
}

interface ComparisonCardsProps {
  options: ComparisonOption[];
  className?: string;
}

export function ComparisonCards({ options, className }: ComparisonCardsProps) {
  const [expanded, setExpanded] = React.useState(false);
  
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="flex justify-end mb-2">
        <button 
          onClick={toggleExpanded}
          className="flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Collapse Details
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Expand Details
            </>
          )}
        </button>
      </div>
      
      <div className={cn("grid grid-cols-1 md:grid-cols-3 gap-4 w-full", className)}>
        {options.map((option, index) => (
          <div 
            key={index}
            className={cn(
              "relative flex flex-col rounded-xl overflow-hidden border shadow-sm transition-all duration-200",
              "p-5",
              expanded ? "h-auto" : "h-full",
              option.recommended 
                ? "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800" 
                : "bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-700"
            )}
          >
            {option.recommended && (
              <div className="absolute top-0 right-0 left-0 bg-indigo-600 text-white text-center py-1 text-sm font-medium">
                RECOMMENDED
              </div>
            )}
            
            <h3 className={cn(
              "text-xl font-semibold mb-4 text-center",
              option.recommended ? "mt-6" : "mt-2",
              option.recommended ? "text-indigo-700 dark:text-indigo-300" : "text-gray-900 dark:text-white"
            )}>
              {option.title}
            </h3>
            
            <div className={cn(
              "space-y-4 flex-1",
              expanded ? "block" : "hidden md:block"
            )}>
              <ComparisonItem label="Money" value={option.money} />
              <ComparisonItem label="Time" value={option.time} />
              <ComparisonItem label="Quality" value={option.quality} />
              <ComparisonItem label="Speed" value={option.speed} />
              <ComparisonItem label="Risk" value={option.risk} />
            </div>
            
            {!expanded && (
              <div className="md:hidden text-center mt-2 text-sm text-indigo-600 dark:text-indigo-400">
                Tap to see details
              </div>
            )}
            
            {option.recommended && (
              <button className="mt-6 w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors">
                Get Started
              </button>
            )}
          </div>
        ))}
      </div>
      
      <div className="md:hidden mt-4 flex justify-center">
        <button 
          onClick={toggleExpanded}
          className="flex items-center justify-center px-4 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-2" />
              Collapse Details
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-2" />
              Expand Details
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function ComparisonItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col space-y-1">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}:</div>
      <div className="text-sm text-gray-600 dark:text-gray-400">{value}</div>
    </div>
  );
}